import * as React from "react";
import "./SelectViewCtl.scss";
import Api from "./Api";
import { IEmployeeView } from "@testout/testout-commerce/models/commerce/EmployeeView";

export interface IEmployeeView2 {
    name: string;
    type: string;
    search?: string;
    filters?: { [field: string]: string[] };
    sort?: { [field: string]: "asc"|"desc" };
    fields?: string[];
    employeeNumber: string;
}

interface ISelectViewProps {
    defaultView: IEmployeeView2;
    view: IEmployeeView2;
    onChange?: (view: IEmployeeView2) => void;
}

interface ISelectViewState {
    isOpen: boolean;
    inProgress: boolean;
    name?: string;
    focusIndex: number;
    views: IEmployeeView2[];
    error: string;
}

export class SelectViewCtl extends React.PureComponent<ISelectViewProps, ISelectViewState> {
    constructor(props: ISelectViewProps) {
        super(props);
        this.state = {
            isOpen: false,
            inProgress: false,
            focusIndex: -1,
            views: [],
            error: ""
        };
    }

    _controlRef = React.createRef<HTMLDivElement>();
    _editRef = React.createRef<HTMLInputElement>();
    _valueRef = React.createRef<HTMLDivElement>();
    _needFocus = false;

    onKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "Enter":
                if (e.currentTarget.tagName === "INPUT")
                    this.onSaveClick();
                else
                    this.onNameClick();
                break;
            case "Escape":
                this.onCancelClick();
                break;
            case "ArrowUp":
                if (this.state.isOpen && this.state.focusIndex > 0)
                    this.setState({ focusIndex: this.state.focusIndex - 1 });
                break;
            case "ArrowDown":
                if (this.state.isOpen && this.state.focusIndex < this.state.views.length)
                    this.setState({ focusIndex: this.state.focusIndex + 1 });
                break;
        }
    }

    private async getViews() {
        let views = (await Api.get<IEmployeeView[]>(`/api/views?employeeNumber=${this.props.defaultView.employeeNumber}&viewType=${this.props.defaultView.type}`))
            .map((v): IEmployeeView2 => ({
                employeeNumber: v.employeeNumber ?? "",
                type: v.viewType ?? "",
                name: v.viewName ?? "",
                filters: JSON.parse(v.filters || "{}"),
                search: v.search,
                sort: JSON.parse(v.sortOrder || "[]"),
                fields: JSON.parse(v.fields || "[]")
            }));
        this.setState({ views: views });
    }

    onNameClick = async () => {
        if (this.state.isOpen)
            this.setState({ isOpen: false });
        else {
            try {
                this.setState({ inProgress: true });
                //await this.getViews();
                this._needFocus = true;
                this.setState({ isOpen: true, focusIndex: -1 });
            }
            catch (e: any) {
                this.setState({ error: e.message });
            }
            finally {
                this.setState({ inProgress: false });
            }
        }
    }

    onEditClick = async () => {
        try {
            this.setState({ inProgress: true });
            await this.getViews();
            this._needFocus = true;
            this.setState({ isOpen: false, error: "", name: this.props.view.name });
        }
        catch (e: any) {
            this.setState({ error: e.message });
        }
        finally {
            this.setState({ inProgress: false });
        }
    }

    onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ name: e.target.value });
    }

    onCancelClick = () => {
        this._needFocus = true;
        this.setState({ name: undefined, error: "" });
    }

    onSaveClick = async () => {
        if (this.canSave)
            try {
                this.setState({ inProgress: true });
                await Api.put<IEmployeeView>('/api/views', {
                    employeeNumber: this.props.defaultView.employeeNumber,
                    viewType: this.props.defaultView.type,
                    viewName: this.state.name,
                    filters: JSON.stringify(this.props.view.filters),
                    search: this.props.view.search,
                    sortOrder: JSON.stringify(this.props.view.sort),
                    fields: JSON.stringify(this.props.view.fields)
                });
                this.props.onChange?.({...this.props.view, name: this.state.name ?? "", employeeNumber: this.props.defaultView.employeeNumber});
                this.setState({ name: undefined });
                this.getViews();
           }
            catch (e: any) {
                this.setState({ error: e.message });
            }
            finally {
                this.setState({ inProgress: false });
            }
    }

    onItemClick = (i: number) => {
        this.props.onChange?.(i < 0 ? this.props.defaultView : this.state.views[i]);
        this.setState({ isOpen: false, error: "" });
    }

    onDeleteClick = async (i: number) => {
        if (i < 0)
            return;
        try {
            this.setState({ inProgress: true });
            await Api.delete(`/api/views/${this.state.views[i].type}/${this.state.views[i].employeeNumber}?viewName=${encodeURIComponent(this.state.views[i].name)}`);
            if (this.state.views[i].name === this.props.view.name)
                this.onItemClick(-1);
            else
                this.setState({ isOpen: false });
            this.getViews();
        }
        catch (e: any) {
            this.setState({ error: e.message });
        }
        finally {
            this.setState({ inProgress: false });
        }
    }

    private get canSave(): boolean {
        return !!this.state.name && this.state.name !== this.props.defaultView.name &&
            !this.state.views.find(v => v.employeeNumber === "SYSTEM" && this.state.name === v.name);
    }

    private get isChanged(): boolean {
        return !this.isEqual(this.props.defaultView, this.props.view) && !this.state.views.find(v => this.isEqual(v, this.props.view));
    }

    private isEqual(v1: IEmployeeView2, v2: IEmployeeView2): boolean {
        return v1.name === v2.name &&
            v1.type === v2.type &&
            v1.search === v2.search &&
            Object.keys(v1.filters ?? {}).every(f =>
                ((v1.filters ?? {})[f] ?? []).length === ((v2.filters ?? {})[f] ?? []).length &&
                ((v1.filters ?? {})[f] ?? []).every(v => ((v2.filters ?? {})[f] ?? []).includes(v))
            ) &&
            Object.keys(v2.filters ?? {}).every(f =>
                ((v2.filters ?? {})[f] ?? []).length === ((v1.filters ?? {})[f] ?? []).length &&
                ((v2.filters ?? {})[f] ?? []).every(v => ((v1.filters ?? {})[f] ?? []).includes(v))
            ) &&
            Object.keys(v1.sort ?? {}).length === Object.keys(v2.sort ?? {}).length &&
            Object.keys(v1.sort ?? {}).every(f => (v1.sort ?? {})[f] === (v2.sort ?? {})[f]) &&
            (v1.fields ?? []).length === (v2.fields ?? []).length &&
            (v1.fields ?? []).every(f => (v2.fields ?? []).includes(f));
    }

    private setFocus() {
        this._needFocus = false;
        const inpEdit = this._editRef.current as HTMLInputElement;
        const divValue = this._valueRef.current as HTMLDivElement;
        if (divValue)
            divValue.focus();
        else
            inpEdit.focus();
    }

    private _canvas: HTMLCanvasElement = document.createElement("canvas");
    private setEditWidth() {
        const inpEdit = this._editRef.current;
        if (inpEdit) {
            const context = this._canvas.getContext("2d");
            if (context) {
                const style = window.getComputedStyle(inpEdit);
                context.font = style.font;
                const width = Math.ceil(context.measureText(this.state.name ?? "").width);
                inpEdit.style.width = `${width + 8}px`;
            }
        }
    }

    onDocumentFocusIn = (e: FocusEvent) => {
        const filter = this._controlRef.current;
        if (filter && !filter.contains(e.target as Node))
            this.setState({ isOpen: false, inProgress: false, focusIndex: -1, error: "" });
    }

    onDocumentClick = (e: FocusEvent) => {
        const filter = this._controlRef.current;
        if (filter && !filter.contains(e.target as Node))
            this.setState({ isOpen: false, inProgress: false, focusIndex: -1, error: "" });
    }

    async componentDidMount() {
        document.addEventListener("click", this.onDocumentClick);
        document.addEventListener("focusin", this.onDocumentFocusIn);
        this.setEditWidth();
        try {
            this.setState({ inProgress: true });
            await this.getViews();
        }
        catch (e: any) {
            this.setState({ error: e.message });
        }
        finally {
            this.setState({ inProgress: false });
        }
    }

    componentDidUpdate() {
        this.setEditWidth();
        if (this._needFocus)
            this.setFocus();
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
        document.removeEventListener("click", this.onDocumentClick);
    }

    render() {
        const { isOpen, inProgress, name, views, focusIndex, error } = this.state;
        const { view, defaultView } = this.props;
        return <div className="select-view" ref={this._controlRef}>
            <div className="name">
                {this.isChanged && <div className="modified">*</div>}
                {name !== undefined 
                    ? <>
                          <input ref={this._editRef} title="Enter a name for the view" className="value" 
                              type="text" value={name} onChange={this.onNameChange} onKeyDown={this.onKeyDown} />
                          <button type="button" title="Save View" disabled={inProgress || !this.canSave}
                              onClick={this.onSaveClick}>&#xe161;</button>
                          <button type="button" title="Cancel" disabled={inProgress} 
                              onClick={this.onCancelClick}>&#xe5cd;</button>
                      </>
                    : <>
                          <div ref={this._valueRef} className="value" tabIndex={0} onKeyDown={this.onKeyDown}
                              onClick={this.onNameClick} onDoubleClick={this.onEditClick}>{view.name}</div>
                          <button type="button" title="Create and save a view using current search and filters" 
                              onClick={this.onEditClick}>&#xe3c9;</button>
                      </>}
               {inProgress && <div className="spinner"></div>}
            </div>
            {!isOpen && !!error && <p className="error">{error}</p>}
            {isOpen && <div className="drop-down scroller">
                <ul>
                    {error
                        ? <li key="_msg" className="error">{error}</li>
                        : <li key="_msg" className="results">Saved Views</li>
                    }
                    {defaultView &&
                        <li key={-1} className={focusIndex === 0 ? "focus" : ""} onClick={() => this.onItemClick(-1)}>
                            {defaultView.name}
                        </li>
                    }
                    {views.map((view, i) =>
                        <li key={i} className={focusIndex === i + 1 ? "focus" : ""} onClick={() => this.onItemClick(i)}>
                            {view.name}
                            {views[i].employeeNumber !== "SYSTEM" && 
                                <button type="button" title="Delete saved view" 
                                    onClick={() => this.onDeleteClick(i)}>&#xe872;</button>}
                        </li>
                    )}
                </ul>
            </div>}
        </div>;
    }
}
